import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import {
  Home, Building2, FlaskConical, Rocket, Shield,
  Send, Globe, Users, Mail, Trophy, BookOpen, User, ShieldAlert,
  MoreHorizontal, X, LogOut, MessageSquare,
} from 'lucide-react'
import { cn } from '../lib/utils'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router'
import { useNotifications } from '../context/NotificationsContext'

// ── Primary nav (always visible) ─────────────────────────────────────────────
const PRIMARY = [
  { to: '/',          label: 'Kommando',  Icon: Home      },
  { to: '/buildings', label: 'Gebäude',   Icon: Building2 },
  { to: '/fleet',     label: 'Flotte',    Icon: Send      },
  { to: '/galaxy',    label: 'Sektor',    Icon: Globe     },
]

// ── All nav groups for drawer ─────────────────────────────────────────────────
const DRAWER_GROUPS = [
  [
    { to: '/shipyard',  label: 'Werft',          Icon: Rocket      },
    { to: '/defense',   label: 'Abwehr',          Icon: Shield      },
    { to: '/research',  label: 'Technologie',     Icon: FlaskConical},
  ],
  [
    { to: '/alliance',  label: 'Koalition',       Icon: Users         },
    { to: '/messages',  label: 'Transmissionen',  Icon: Mail          },
    { to: '/chat',      label: 'Chat',             Icon: MessageSquare },
  ],
  [
    { to: '/highscore', label: 'Rangliste',        Icon: Trophy      },
    { to: '/help',      label: 'Codex',            Icon: BookOpen    },
    { to: '/bruecke',   label: 'Brücke',           Icon: User        },
    { to: '/admin',     label: 'Admin',            Icon: ShieldAlert },
  ],
]

export function BottomNav() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const { unread, chatUnread } = useNotifications()

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) return
      supabase.from('profiles').select('is_admin').eq('id', data.user.id).maybeSingle()
        .then(({ data: p }) => setIsAdmin(p?.is_admin ?? false))
    })
  }, [])

  // Close drawer on route change
  useEffect(() => { setOpen(false) }, [pathname])

  function isActive(to: string) {
    if (to === '/') return pathname === '/'
    return pathname.startsWith(to)
  }

  async function handleLogout() {
    await supabase.auth.signOut()
    navigate('/login')
  }

  return (
    <>
      {/* ── Drawer backdrop ───────────────────────────────────────────────── */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* ── Drawer sheet ─────────────────────────────────────────────────── */}
      <div
        className={cn(
          'fixed left-0 right-0 z-50 md:hidden transition-transform duration-200 bg-space-950 border-t border-crystal-400/30',
          open ? 'bottom-14 translate-y-0' : 'bottom-14 translate-y-full pointer-events-none',
        )}
        style={{ boxShadow: '0 -8px 32px rgba(103,232,249,0.08)' }}
      >
        {/* Close bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-crystal-400/15">
          <span className="font-mono text-[10px] tracking-widest uppercase text-crystal-400/50">Navigation</span>
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-300 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav groups */}
        <div className="px-3 py-3 space-y-3">
          {DRAWER_GROUPS.map((group, gi) => (
            <div key={gi} className="grid grid-cols-3 gap-2">
              {group
                .filter(({ to }) => to !== '/admin' || isAdmin)
                .map(({ to, label, Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/'}
                    className={({ isActive }) => cn(
                      'relative flex flex-col items-center gap-1 py-3 border text-xs font-mono tracking-wide uppercase transition-all',
                      isActive
                        ? 'border-nova-400/60 text-nova-300 bg-nova-600/15'
                        : 'border-space-600/50 text-slate-400 hover:text-crystal-400 hover:border-crystal-400/30',
                    )}
                  >
                    <span className="relative">
                      <Icon className="w-5 h-5" />
                      {to === '/messages' && unread > 0 && (
                        <span className="absolute -top-1 -right-2 font-mono text-[8px] bg-amber-500 text-white leading-none px-1 py-0.5 min-w-[14px] text-center">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                      {to === '/chat' && chatUnread > 0 && (
                        <span className="absolute -top-1 -right-2 font-mono text-[8px] bg-amber-500 text-white leading-none px-1 py-0.5 min-w-[14px] text-center">
                          {chatUnread > 9 ? '9+' : chatUnread}
                        </span>
                      )}
                    </span>
                    <span className="text-[9px] leading-none">{label}</span>
                  </NavLink>
                ))}
            </div>
          ))}

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-2 border border-space-600/40 text-slate-400 hover:text-crystal-400 hover:border-crystal-400/30 font-mono text-xs tracking-widest uppercase transition-all"
          >
            <LogOut className="w-4 h-4" />
            Abmelden
          </button>
        </div>
      </div>

      {/* ── Bottom bar ───────────────────────────────────────────────────── */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-space-950 border-t border-crystal-400/25 md:hidden"
        style={{ boxShadow: '0 -4px 20px rgba(103,232,249,0.06)' }}
      >
        <ul className="flex items-stretch h-14">
          {PRIMARY.map(({ to, label, Icon }) => {
            const active = isActive(to)
            return (
              <li key={to} className="flex-1">
                <Link
                  to={to}
                  className={cn(
                    'flex flex-col items-center justify-center h-full gap-0.5 transition-colors duration-150',
                    active ? 'text-nova-400' : 'text-slate-400 hover:text-slate-300',
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] font-mono leading-none">{label}</span>
                </Link>
              </li>
            )
          })}

          {/* More button */}
          <li className="flex-1">
            <button
              onClick={() => setOpen(v => !v)}
              className={cn(
                'flex flex-col items-center justify-center w-full h-full gap-0.5 transition-colors duration-150',
                open ? 'text-crystal-400' : 'text-slate-400 hover:text-slate-300',
              )}
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-[10px] font-mono leading-none">Mehr</span>
            </button>
          </li>
        </ul>
      </nav>
    </>
  )
}
